/**
 * Sound Story Street (lesson 9): phonics games on the light stage.
 * Big letter tiles, drag-or-tap into slots, the spoken sound on every touch,
 * and the handwriting pad below for the "write it" step.
 */
import React, { useEffect, useMemo, useState } from "react";
import WritingHandwritingPad from "../WritingHandwritingPad";
import { WRITING_L9 } from "../../../../data/writingCurriculum";
import { useDragEngine, useDropTarget } from "../../interaction/useDragEngine";
import { ConfettiBurst } from "../../feedback/Celebration";
import { useBreakpoint } from "../../shell/LearnShell";
import { LEARN } from "../../theme";
import {
  ActionButton,
  Arena,
  Board,
  Caption,
  LetterTile,
  PadDock,
  Panel,
  PictureTile,
  Rail,
  RoundPill,
  Slot,
  SpeakerButton,
  SpeakerIcon,
  WordCard,
  WordSlot,
  darken,
  seededShuffle,
  useBurst,
  useCompleteOnce,
  useLater,
  useRoundCue,
  useShake,
  wash,
} from "./arenaKit";

const cue = (letter) => WRITING_L9.soundCues[letter] || letter;
const stretched = (word) => `${(word.sounds || word.word.split("")).map(cue).join("... ")}. ${word.word}.`;
const DISTRACTOR_POOL = "bdfgjklmnprstwz".split("");

const useCompact = () => {
  const bp = useBreakpoint();
  return bp === "xs" || bp === "sm";
};

/** Square drop slot that registers itself with the drag engine. */
function DropSlot({ registerTarget, id, index, ...rest }) {
  const data = useMemo(() => ({ index }), [index]);
  const ref = useDropTarget(registerTarget, id, data);
  return <Slot ref={ref} index={index + 1} {...rest} />;
}

function DropWordSlot({ registerTarget, id, kind, ...rest }) {
  const data = useMemo(() => ({ kind }), [kind]);
  const ref = useDropTarget(registerTarget, id, data);
  return <WordSlot ref={ref} {...rest} />;
}

/** Handwriting step shared by the sound games. */
function WritePad({ target, accent, mode = "word", onAccepted, onWrong, resetKey }) {
  return (
    <PadDock>
      <WritingHandwritingPad
        key={resetKey ?? target}
        target={target}
        accent={accent}
        showsGuide={false}
        mode={mode}
        onAccepted={onAccepted}
        onRejected={() => onWrong?.()}
      />
    </PadDock>
  );
}

// ── Sound to Letter ──────────────────────────────────────────────────────────

export const SoundToLetterGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const rounds = WRITING_L9.soundToLetterRounds;
  const accent = activity.accent;
  const [index, setIndex] = useState(0);
  const [solved, setSolved] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();
  const later = useLater();
  const round = rounds[Math.min(index, rounds.length - 1)];
  const soundLine = `Listen. ${cue(round.target)}.`;
  useRoundCue(speak, soundLine, index);

  const choose = (letter) => {
    if (solved) return;
    sfx?.("pop", { volume: 0.5 });
    if (letter !== round.target) {
      onWrong?.();
      shake(letter);
      return;
    }
    setSolved(letter);
    sfx?.("correct");
    fire();
    speak?.(`${cue(letter)}. ${letter}.`, { interrupt: true, dedupe: false });
    later(() => {
      if (index + 1 >= rounds.length) complete();
      else {
        setIndex((n) => n + 1);
        setSolved(null);
      }
    }, 950);
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={20} originY={28} reduceMotion={reduceMotion} />
      <Board
        title={activity.title}
        symbol={activity.symbol}
        accent={accent}
        right={<RoundPill done={Math.min(index + 1, rounds.length)} total={rounds.length} accent={accent} />}
        bodyStyle={{ gap: 22 }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <SpeakerButton
            accent={accent}
            size={72}
            pulse={!solved}
            reduceMotion={reduceMotion}
            ariaLabel="Hear the sound"
            onClick={() => speak?.(soundLine, { interrupt: true, dedupe: false })}
          />
          <Caption>Tap to hear the sound again</Caption>
        </div>
        <Rail gap={16}>
          {round.choices.map((letter) => (
            <LetterTile
              key={`${index}-${letter}`}
              label={letter}
              ariaLabel={`Letter ${letter}`}
              accent={accent}
              size={1.2}
              tone={solved === letter ? "correct" : isShaking(letter) ? "wrong" : "idle"}
              shaking={isShaking(letter)}
              pop={solved === letter}
              reduceMotion={reduceMotion}
              onClick={() => choose(letter)}
              disabled={!!solved && solved !== letter}
              style={!reduceMotion && !solved && !isShaking(letter) ? { animation: "wt-rise 0.36s ease both" } : undefined}
            />
          ))}
        </Rail>
      </Board>
    </Arena>
  );
};

// ── Which Sound? ─────────────────────────────────────────────────────────────

export const WhichSoundGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const rounds = WRITING_L9.whichSoundRounds;
  const accent = activity.accent;
  const compact = useCompact();
  const [index, setIndex] = useState(0);
  const [solved, setSolved] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();
  const later = useLater();
  const round = rounds[Math.min(index, rounds.length - 1)];
  const names = ["One", "Two", "Three"];
  const allLine = round.sounds.map((s, i) => `${names[i]}. ${cue(s)}.`).join(" ");
  useRoundCue(speak, `Letter ${round.letter}. ${allLine}`, index);

  const choose = (sound, i) => {
    if (solved != null) return;
    sfx?.("pop", { volume: 0.5 });
    speak?.(cue(sound), { interrupt: true, dedupe: false });
    if (sound !== round.letter) {
      onWrong?.();
      shake(i);
      return;
    }
    setSolved(i);
    sfx?.("correct");
    fire();
    later(() => {
      if (index + 1 >= rounds.length) complete();
      else {
        setIndex((n) => n + 1);
        setSolved(null);
      }
    }, 1000);
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={20} originY={28} reduceMotion={reduceMotion} />
      <Board
        title={activity.title}
        symbol={activity.symbol}
        accent={accent}
        right={<RoundPill done={Math.min(index + 1, rounds.length)} total={rounds.length} accent={accent} />}
        bodyStyle={{ gap: 22 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: compact ? 12 : 18, flexWrap: "wrap", justifyContent: "center" }}>
          <LetterTile label={round.letter} as="div" accent={accent} size={compact ? 1.1 : 1.3} tone="hint" reduceMotion={reduceMotion} />
          <ActionButton accent={accent} size={compact ? "sm" : "md"} reduceMotion={reduceMotion} icon={<SpeakerIcon size={18} />} onClick={() => speak?.(allLine, { interrupt: true, dedupe: false })}>
            {compact ? "Play" : "Play the sounds"}
          </ActionButton>
        </div>
        <Rail gap={18}>
          {round.sounds.map((sound, i) => (
            <div key={`${index}-${i}`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <LetterTile
                ariaLabel={`Sound ${names[i]}`}
                accent={accent}
                size={1.2}
                tone={solved === i ? "correct" : isShaking(i) ? "wrong" : "idle"}
                shaking={isShaking(i)}
                pop={solved === i}
                reduceMotion={reduceMotion}
                onClick={() => choose(sound, i)}
                disabled={solved != null && solved !== i}
              >
                <SpeakerIcon size={compact ? 30 : 38} color={solved === i ? "#fff" : darken(accent, 0.1)} />
              </LetterTile>
              <span style={{ fontWeight: 700, fontSize: 16, color: LEARN.color.muted }}>{i + 1}</span>
            </div>
          ))}
        </Rail>
      </Board>
    </Arena>
  );
};

// ── First Sound, Last Sound ──────────────────────────────────────────────────

export const FirstAndLastGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const rounds = WRITING_L9.firstLastRounds;
  const accent = activity.accent;
  const compact = useCompact();
  const [index, setIndex] = useState(0);
  const [filled, setFilled] = useState(false);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const later = useLater();
  const round = rounds[Math.min(index, rounds.length - 1)];
  const { word } = round;
  const letters = word.word.split("");
  const blankAt = round.position === "first" ? 0 : letters.length - 1;
  useRoundCue(speak, `${word.word}. Write the ${round.position} sound you hear.`, index);

  const accepted = () => {
    if (filled) return;
    setFilled(true);
    sfx?.("correct");
    fire();
    speak?.(`${cue(round.answer)}. ${word.word}.`, { interrupt: true, dedupe: false });
    later(() => {
      if (index + 1 >= rounds.length) complete();
      else {
        setIndex((n) => n + 1);
        setFilled(false);
      }
    }, 900);
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <PictureTile
          name={word.assetName}
          alt={word.word}
          accent={accent}
          size={compact ? 1.2 : 1.5}
          ariaLabel={`Hear ${word.word}`}
          reduceMotion={reduceMotion}
          onClick={() => {
            sfx?.("pop", { volume: 0.4 });
            speak?.(word.word, { interrupt: true, dedupe: false });
          }}
        />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          <Caption style={{ textAlign: "left" }}>{round.position === "first" ? "First sound" : "Last sound"}</Caption>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {letters.map((ch, i) =>
              i === blankAt ? (
                <Slot key={i} accent={accent} size={0.75} filled={filled} active ariaLabel={`${round.position} sound`} reduceMotion={reduceMotion} onClick={() => speak?.(`${cue(round.answer)}.`, { interrupt: true, dedupe: false })}>
                  {filled && <LetterTile label={round.answer} as="div" accent={accent} size={0.75} tone="correct" pop reduceMotion={reduceMotion} />}
                </Slot>
              ) : (
                <LetterTile key={i} label={ch} as="div" accent={accent} size={0.75} tone="quiet" reduceMotion={reduceMotion} />
              ),
            )}
          </div>
        </div>
        <RoundPill done={Math.min(index + 1, rounds.length)} total={rounds.length} accent={accent} />
      </Panel>
      <WritePad target={round.answer} mode="letter" accent={accent} onAccepted={accepted} onWrong={onWrong} resetKey={`${index}-${round.answer}`} />
    </Arena>
  );
};

// ── Build boards (Sound Ladder · Word Builder) ───────────────────────────────

function BuildBoard({ word, tray, accent, sfx, speak, onWrong, reduceMotion, onBuilt, ladder, title, symbol, counter }) {
  const pieces = word.sounds;
  const compact = useCompact();
  const [placed, setPlaced] = useState({});
  const [picked, setPicked] = useState(null);
  const { shake, isShaking } = useShake();
  const later = useLater();
  const placedIds = useMemo(() => new Set(Object.values(placed)), [placed]);
  const doneCount = Object.keys(placed).length;

  const tryPlace = (item, slotIndex) => {
    if (placed[slotIndex] != null) return false;
    if (item.letter !== pieces[slotIndex]) {
      onWrong?.();
      shake(`slot-${slotIndex}`);
      return false;
    }
    const next = { ...placed, [slotIndex]: item.id };
    setPlaced(next);
    setPicked(null);
    sfx?.("snap");
    if (Object.keys(next).length >= pieces.length) {
      sfx?.("correct");
      speak?.(`${word.word}.`, { interrupt: true, dedupe: false });
      later(onBuilt, 900);
    }
    return true;
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId, targetData }) => {
      if (!targetId) return false;
      return tryPlace(payload, targetData.index);
    },
  });

  const tapTray = (item) => {
    sfx?.("pop", { volume: 0.5 });
    speak?.(cue(item.letter), { interrupt: true, dedupe: false });
    setPicked((p) => (p === item.id ? null : item.id));
  };

  const tapSlot = (slotIndex) => {
    const item = tray.find((t) => t.id === picked);
    if (!item) {
      if (placed[slotIndex] != null) speak?.(cue(pieces[slotIndex]), { interrupt: true, dedupe: false });
      return;
    }
    tryPlace(item, slotIndex);
  };

  return (
    <>
      <Panel style={{ display: "flex", alignItems: "center", gap: compact ? 12 : 18, flexWrap: "wrap", padding: compact ? 12 : "14px 18px" }}>
        <PictureTile
          name={word.assetName}
          alt={word.word}
          accent={accent}
          size={compact ? 1.2 : 1.45}
          ariaLabel={`Hear ${word.word}`}
          reduceMotion={reduceMotion}
          onClick={() => {
            sfx?.("pop", { volume: 0.4 });
            speak?.(word.word, { interrupt: true, dedupe: false });
          }}
        />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {ladder && <Caption style={{ textAlign: "left" }}>Climb the ladder, one sound at a time</Caption>}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            {pieces.map((_, i) => {
              const item = tray.find((t) => t.id === placed[i]);
              return (
                <DropSlot
                  key={i}
                  registerTarget={registerTarget}
                  id={`slot-${i}`}
                  index={i}
                  accent={accent}
                  size={1}
                  hover={hoverId === `slot-${i}`}
                  filled={!!item}
                  active={!!picked && !item}
                  shaking={isShaking(`slot-${i}`)}
                  reduceMotion={reduceMotion}
                  ariaLabel={item ? `Sound ${i + 1}: ${item.letter}` : `Sound ${i + 1}`}
                  onClick={() => tapSlot(i)}
                  style={ladder ? { marginBottom: `calc(var(--tile) * ${0.18 * (pieces.length - 1 - i)})` } : undefined}
                >
                  {item && <LetterTile label={item.letter} as="div" accent={accent} tone="correct" pop reduceMotion={reduceMotion} />}
                </DropSlot>
              );
            })}
          </div>
        </div>
        {counter && <div style={{ alignSelf: "flex-start", marginLeft: "auto" }}>{counter}</div>}
      </Panel>
      <Board title={title} symbol={symbol} accent={accent} caption={ladder ? "Tap a sound, then its rung" : "Tap a letter, then its spot"}>
        <Rail gap={compact ? 12 : 16}>
          {tray.map((item) => {
            const used = placedIds.has(item.id);
            return (
              <LetterTile
                key={item.id}
                label={item.letter}
                ariaLabel={`Letter ${item.letter}`}
                accent={accent}
                size={compact ? 1 : 1.15}
                tone={used ? "blank" : picked === item.id ? "selected" : "idle"}
                reduceMotion={reduceMotion}
                disabled={used || doneCount >= pieces.length}
                drag={used ? undefined : dragProps(item, (p) => <LetterTile label={p.letter} as="div" accent={accent} size={compact ? 1 : 1.15} tone="selected" />)}
                onClick={() => tapTray(item)}
                style={used ? { opacity: 0.35 } : undefined}
              />
            );
          })}
        </Rail>
      </Board>
      <GhostLayer />
    </>
  );
}

function BuildThenWrite({ activity, speak, sfx, onComplete, onWrong, reduceMotion, words, trayFor, ladder }) {
  const accent = activity.accent;
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("build");
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const word = words[Math.min(index, words.length - 1)];
  const tray = useMemo(() => trayFor(word, index), [trayFor, word, index]);
  useRoundCue(speak, `${word.word}.`, index);

  const counter = <RoundPill done={Math.min(index + 1, words.length)} total={words.length} accent={accent} label={phase === "build" ? "Build" : "Write"} />;

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={20} originY={24} reduceMotion={reduceMotion} />
      {phase === "build" ? (
        <BuildBoard
          key={`${index}-${word.word}`}
          word={word}
          tray={tray}
          accent={accent}
          sfx={sfx}
          speak={speak}
          onWrong={onWrong}
          reduceMotion={reduceMotion}
          ladder={ladder}
          title={activity.title}
          symbol={activity.symbol}
          counter={counter}
          onBuilt={() => setPhase("write")}
        />
      ) : (
        <>
          <Panel style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <PictureTile name={word.assetName} alt={word.word} accent={accent} size={1.1} as="div" tone="quiet" />
            <Rail gap={6} style={{ justifyContent: "flex-start", flex: 1 }}>
              {word.sounds.map((s, i) => (
                <LetterTile key={i} label={s} as="div" accent={accent} size={0.75} tone="correct" reduceMotion={reduceMotion} />
              ))}
            </Rail>
            <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel={`Hear ${word.word}`} onClick={() => speak?.(stretched(word), { interrupt: true, dedupe: false })} />
            {counter}
          </Panel>
          <WritePad
            target={word.word}
            accent={accent}
            onWrong={onWrong}
            resetKey={`${index}-${word.word}`}
            onAccepted={() => {
              sfx?.("correct");
              fire();
              if (index + 1 >= words.length) complete();
              else {
                setIndex((n) => n + 1);
                setPhase("build");
              }
            }}
          />
        </>
      )}
    </Arena>
  );
}

const ladderTray = (word, index) => seededShuffle(word.sounds.map((letter, i) => ({ id: `${letter}-${i}`, letter })), index + 3);
const builderTray = (word, index) =>
  seededShuffle(
    word.word
      .split("")
      .concat(["m", "s", "p"])
      .map((letter, i) => ({ id: `${letter}-${i}`, letter })),
    index + 11,
  );

export const SoundLadderGame = (props) => <BuildThenWrite {...props} words={WRITING_L9.ladderWords} trayFor={ladderTray} ladder />;
export const WordBuilderGame = (props) => <BuildThenWrite {...props} words={WRITING_L9.builderWords} trayFor={builderTray} />;

// ── Change One Sound ─────────────────────────────────────────────────────────

export const ChangeOneSoundGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const rounds = WRITING_L9.changeRounds;
  const accent = activity.accent;
  const [index, setIndex] = useState(0);
  const [changed, setChanged] = useState(false);
  const [picked, setPicked] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();
  const later = useLater();
  const round = rounds[Math.min(index, rounds.length - 1)];
  const letters = (changed ? round.to : round.from).split("");
  const newLetter = round.to[round.changedIndex];
  const tray = useMemo(() => {
    const pool = DISTRACTOR_POOL.filter((l) => !round.from.includes(l) && !round.to.includes(l));
    const picks = seededShuffle(pool, index + 5).slice(0, 2);
    return seededShuffle([newLetter, ...picks].map((letter, i) => ({ id: `${letter}-${i}`, letter })), index + 9);
  }, [round, index, newLetter]);
  useRoundCue(speak, `Change ${round.from} to ${round.to}. Listen: ${round.to}.`, index);

  const tryPlace = (item, position) => {
    if (changed) return false;
    if (position !== round.changedIndex || item.letter !== newLetter) {
      onWrong?.();
      shake(`pos-${position}`);
      return false;
    }
    setChanged(true);
    setPicked(null);
    sfx?.("snap");
    fire();
    speak?.(`${round.to}.`, { interrupt: true, dedupe: false });
    return true;
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId, targetData }) => (targetId ? tryPlace(payload, targetData.index) : false),
  });

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={22} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <SpeakerButton accent={accent} size={52} reduceMotion={reduceMotion} ariaLabel={`Hear ${round.to}`} onClick={() => speak?.(`${round.to}.`, { interrupt: true, dedupe: false })} />
        <div style={{ flex: 1, display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          {letters.map((ch, i) => (
            <DropSlot
              key={`${index}-${i}`}
              registerTarget={registerTarget}
              id={`pos-${i}`}
              index={i}
              accent={accent}
              size={1}
              filled
              hover={hoverId === `pos-${i}`}
              shaking={isShaking(`pos-${i}`)}
              reduceMotion={reduceMotion}
              ariaLabel={`Letter ${ch}, position ${i + 1}`}
              onClick={() => {
                const item = tray.find((t) => t.id === picked);
                if (item) tryPlace(item, i);
                else speak?.(cue(ch), { interrupt: true, dedupe: false });
              }}
            >
              <LetterTile
                label={ch}
                as="div"
                accent={accent}
                tone={changed && i === round.changedIndex ? "correct" : hoverId === `pos-${i}` ? "hint" : "idle"}
                pop={changed && i === round.changedIndex}
                reduceMotion={reduceMotion}
              />
            </DropSlot>
          ))}
        </div>
        <RoundPill done={Math.min(index + 1, rounds.length)} total={rounds.length} accent={accent} />
      </Panel>
      {!changed ? (
        <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="Which letter makes the new word?">
          <Rail gap={16}>
            {tray.map((item) => (
              <LetterTile
                key={item.id}
                label={item.letter}
                ariaLabel={`Letter ${item.letter}`}
                accent={accent}
                size={1.2}
                tone={picked === item.id ? "selected" : "idle"}
                reduceMotion={reduceMotion}
                drag={dragProps(item, (p) => <LetterTile label={p.letter} as="div" accent={accent} size={1.2} tone="selected" />)}
                onClick={() => {
                  sfx?.("pop", { volume: 0.5 });
                  speak?.(cue(item.letter), { interrupt: true, dedupe: false });
                  setPicked((p) => (p === item.id ? null : item.id));
                }}
              />
            ))}
          </Rail>
        </Board>
      ) : (
        <WritePad
          target={round.to}
          accent={accent}
          onWrong={onWrong}
          resetKey={`${index}-${round.to}`}
          onAccepted={() => {
            sfx?.("correct");
            later(() => {
              if (index + 1 >= rounds.length) complete();
              else {
                setIndex((n) => n + 1);
                setChanged(false);
              }
            }, 300);
          }}
        />
      )}
      <GhostLayer />
    </Arena>
  );
};

// ── Stretch & Write ──────────────────────────────────────────────────────────

export const StretchAndWriteGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const accent = activity.accent;
  const compact = useCompact();
  const [picked, setPicked] = useState([]);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("pick");
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const later = useLater();
  const word = picked[Math.min(index, Math.max(0, picked.length - 1))];

  useEffect(() => {
    if (phase !== "write" || !word) return undefined;
    const t = setTimeout(() => speak?.(stretched(word), { interrupt: true, dedupe: false }), 380);
    return () => clearTimeout(t);
  }, [phase, word, speak]);

  const pick = (item) => {
    if (picked.find((p) => p.word === item.word)) return;
    sfx?.("pop", { volume: 0.5 });
    speak?.(item.word, { interrupt: true, dedupe: false });
    const next = [...picked, item];
    setPicked(next);
    if (next.length >= 4) {
      sfx?.("sparkle");
      later(() => setPhase("write"), 700);
    }
  };

  if (phase === "pick") {
    return (
      <Arena>
        <Board
          title={activity.title}
          symbol={activity.symbol}
          accent={accent}
          right={<RoundPill done={picked.length} total={4} accent={accent} label="Picked" />}
          caption="Pick four pictures, in any order"
        >
          <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(calc(var(--tile) * ${compact ? 1.3 : 1.45}), max-content))`, gap: compact ? 12 : 16, width: "100%", justifyContent: "center" }}>
            {WRITING_L9.stretchChoices.map((item) => {
              const at = picked.findIndex((p) => p.word === item.word);
              return (
                <PictureTile
                  key={item.word}
                  name={item.assetName}
                  alt={item.word}
                  accent={accent}
                  size={compact ? 1.3 : 1.45}
                  tone={at >= 0 ? "selected" : "idle"}
                  badge={at >= 0 ? at + 1 : undefined}
                  reduceMotion={reduceMotion}
                  ariaLabel={at >= 0 ? `${item.word}, picked ${at + 1}` : `Pick ${item.word}`}
                  onClick={() => pick(item)}
                />
              );
            })}
          </div>
        </Board>
      </Arena>
    );
  }

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <PictureTile
          name={word.assetName}
          alt={word.word}
          accent={accent}
          size={compact ? 1.2 : 1.5}
          ariaLabel={`Hear ${word.word}`}
          reduceMotion={reduceMotion}
          onClick={() => {
            sfx?.("pop", { volume: 0.4 });
            speak?.(word.word, { interrupt: true, dedupe: false });
          }}
        />
        <ActionButton accent={accent} reduceMotion={reduceMotion} icon={<SpeakerIcon size={18} />} onClick={() => speak?.(stretched(word), { interrupt: true, dedupe: false })}>
          Stretch it
        </ActionButton>
        <div style={{ flex: 1 }} />
        <RoundPill done={Math.min(index + 1, picked.length)} total={picked.length} accent={accent} />
      </Panel>
      <WritePad
        target={word.word}
        accent={accent}
        onWrong={onWrong}
        resetKey={`${index}-${word.word}`}
        onAccepted={() => {
          sfx?.("correct");
          fire();
          later(() => {
            if (index + 1 >= picked.length) complete();
            else setIndex((n) => n + 1);
          }, 300);
        }}
      />
    </Arena>
  );
};

// ── Word Wall ────────────────────────────────────────────────────────────────

const patternHighlight = (item) => {
  if (item.pattern.startsWith("-")) return item.pattern.slice(1);
  if (item.pattern === "magic e") return "e";
  return "";
};

export const WordWallGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const wall = WRITING_L9.wordWall;
  const accent = activity.accent;
  const compact = useCompact();
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("words");
  const [chosen, setChosen] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const later = useLater();
  const item = wall[Math.min(index, wall.length - 1)];
  const sentence = chosen ? `I see a ${chosen.word}` : "";
  useRoundCue(speak, phase === "words" ? `${item.word}.` : "", index);

  const hl = patternHighlight(item);
  const hlStart = hl ? item.word.lastIndexOf(hl) : -1;

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {wall.map((brick, i) => {
            const done = phase === "sentence" || i < index;
            const active = phase === "words" ? i === index : chosen?.word === brick.word;
            return (
              <WordCard
                key={brick.word}
                text={done ? brick.word : brick.pattern}
                ariaLabel={phase === "sentence" ? `Use ${brick.word}` : `${brick.pattern} brick`}
                accent={accent}
                size="sm"
                tone={active ? "selected" : done ? "correct" : "quiet"}
                reduceMotion={reduceMotion}
                onClick={
                  phase === "sentence"
                    ? () => {
                        sfx?.("pop", { volume: 0.5 });
                        setChosen(brick);
                        speak?.(`I see a ${brick.word}.`, { interrupt: true, dedupe: false });
                      }
                    : () => speak?.(brick.pattern, { interrupt: true, dedupe: false })
                }
                style={{ flex: 1, minWidth: 0, padding: "8px 6px" }}
              />
            );
          })}
        </div>
        {phase === "words" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {item.assetName ? (
              <PictureTile
                name={item.assetName}
                alt={item.word}
                accent={accent}
                size={compact ? 1.1 : 1.3}
                ariaLabel={`Hear ${item.word}`}
                reduceMotion={reduceMotion}
                onClick={() => speak?.(item.word, { interrupt: true, dedupe: false })}
              />
            ) : (
              <SpeakerButton accent={accent} size={52} reduceMotion={reduceMotion} ariaLabel={`Hear ${item.word}`} onClick={() => speak?.(item.word, { interrupt: true, dedupe: false })} />
            )}
            <div style={{ flex: 1, fontSize: compact ? 30 : 38, fontWeight: 700, letterSpacing: "0.04em", lineHeight: 1 }} aria-label={item.word}>
              {hlStart >= 0 ? (
                <>
                  {item.word.slice(0, hlStart)}
                  <span style={{ color: darken(accent, 0.1), background: wash(accent, 0.16), borderRadius: 10, padding: "0 6px" }}>{hl}</span>
                  {item.word.slice(hlStart + hl.length)}
                </>
              ) : (
                item.word
              )}
            </div>
            <RoundPill done={Math.min(index + 1, wall.length)} total={wall.length} accent={accent} />
          </div>
        ) : (
          <Rail>
            <WordCard text="I" as="div" accent={accent} />
            <WordCard text="see" as="div" accent={accent} />
            <WordCard text="a" as="div" accent={accent} />
            {chosen ? <WordCard text={chosen.word} as="div" accent={accent} tone="selected" pop reduceMotion={reduceMotion} /> : <WordSlot accent={accent} label="tap a word" active />}
          </Rail>
        )}
      </Panel>
      {phase === "words" ? (
        <WritePad
          target={item.word}
          accent={accent}
          onWrong={onWrong}
          resetKey={`${index}-${item.word}`}
          onAccepted={() => {
            sfx?.("correct");
            fire();
            later(() => {
              if (index + 1 >= wall.length) setPhase("sentence");
              else setIndex((n) => n + 1);
            }, 300);
          }}
        />
      ) : chosen ? (
        <WritePad
          target={sentence}
          accent={accent}
          onWrong={onWrong}
          resetKey={sentence}
          onAccepted={() => {
            sfx?.("correct");
            fire();
            later(complete, 300);
          }}
        />
      ) : (
        <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="Tap a word to put it in the sentence" minHeight="clamp(120px, calc(var(--arena-h, 600px) * 0.2), 200px)">
          <Rail gap={10}>
            {wall.map((brick) => (
              <WordCard
                key={`pick-${brick.word}`}
                text={brick.word}
                ariaLabel={`Put ${brick.word} in the sentence`}
                accent={accent}
                size="lg"
                reduceMotion={reduceMotion}
                onClick={() => {
                  sfx?.("pop", { volume: 0.5 });
                  setChosen(brick);
                  speak?.(`I see a ${brick.word}.`, { interrupt: true, dedupe: false });
                }}
              />
            ))}
          </Rail>
        </Board>
      )}
    </Arena>
  );
};

// ── Your Choice in a Sentence ────────────────────────────────────────────────

export const SentenceChoiceGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const accent = activity.accent;
  const compact = useCompact();
  const [count, setCount] = useState(0);
  const [noun, setNoun] = useState(null);
  const [color, setColor] = useState(null);
  const [picked, setPicked] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();
  const later = useLater();
  const sentence = noun && color ? `The ${noun.word} is ${color.word}` : "";

  const place = (payload, kind) => {
    if (payload.kind !== kind) {
      onWrong?.();
      shake(kind);
      return false;
    }
    sfx?.("snap");
    setPicked(null);
    if (kind === "noun") setNoun(payload.item);
    else setColor(payload.item);
    const n = kind === "noun" ? payload.item : noun;
    const c = kind === "color" ? payload.item : color;
    if (n && c) speak?.(`The ${n.word} is ${c.word}.`, { interrupt: true, dedupe: false });
    else speak?.(payload.item.word, { interrupt: true, dedupe: false });
    return true;
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId, targetData }) => (targetId ? place(payload, targetData.kind) : false),
  });

  const stage = !noun ? "noun" : !color ? "color" : "write";
  const choices = stage === "noun" ? WRITING_L9.sentenceNouns : WRITING_L9.sentenceColors;

  const slot = (kind, value) => (
    <DropWordSlot
      key={kind}
      registerTarget={registerTarget}
      id={`slot-${kind}`}
      kind={kind}
      accent={accent}
      hover={hoverId === `slot-${kind}`}
      active={!!picked && !value}
      shaking={isShaking(kind)}
      reduceMotion={reduceMotion}
      label={kind === "noun" ? "picture word" : "color"}
      ariaLabel={value ? value.word : `${kind === "noun" ? "picture word" : "color"} slot`}
      onClick={() => {
        if (picked) place(picked, kind);
        else if (value) speak?.(value.word, { interrupt: true, dedupe: false });
      }}
    >
      {value && <WordCard text={value.word} as="div" accent={accent} tone="selected" pop reduceMotion={reduceMotion} />}
    </DropWordSlot>
  );

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <Caption style={{ textAlign: "left" }}>{stage === "noun" ? "Choose a picture word" : stage === "color" ? "Choose a color" : "Now write it"}</Caption>
          <RoundPill done={Math.min(count + 1, 3)} total={3} accent={accent} label="Sentence" />
        </div>
        <Rail gap={8}>
          <WordCard text="The" as="div" accent={accent} />
          {slot("noun", noun)}
          <WordCard text="is" as="div" accent={accent} />
          {slot("color", color)}
          {sentence && <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the sentence" onClick={() => speak?.(`${sentence}.`, { interrupt: true, dedupe: false })} />}
        </Rail>
      </Panel>
      {stage !== "write" ? (
        <Board title={activity.title} symbol={activity.symbol} accent={accent}>
          <Rail gap={compact ? 12 : 16}>
            {choices.map((item) => {
              const payload = { kind: stage, item };
              return (
                <PictureTile
                  key={item.word}
                  name={item.assetName}
                  alt={item.word}
                  label={item.word}
                  accent={accent}
                  size={compact ? 1.4 : 1.6}
                  tone={picked?.item?.word === item.word ? "selected" : "idle"}
                  reduceMotion={reduceMotion}
                  ariaLabel={`Choose ${item.word}`}
                  drag={dragProps(payload, (p) => <PictureTile name={p.item.assetName} alt={p.item.word} label={p.item.word} accent={accent} size={compact ? 1.4 : 1.6} tone="selected" as="div" />)}
                  onClick={() => {
                    sfx?.("pop", { volume: 0.5 });
                    speak?.(item.word, { interrupt: true, dedupe: false });
                    setPicked((p) => (p?.item?.word === item.word ? null : payload));
                  }}
                />
              );
            })}
          </Rail>
        </Board>
      ) : (
        <WritePad
          target={sentence}
          accent={accent}
          onWrong={onWrong}
          resetKey={`${sentence}-${count}`}
          onAccepted={() => {
            sfx?.("correct");
            fire();
            later(() => {
              if (count + 1 >= 3) complete();
              else {
                setCount((n) => n + 1);
                setNoun(null);
                setColor(null);
              }
            }, 300);
          }}
        />
      )}
      <GhostLayer />
    </Arena>
  );
};
